import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';
import { ThemeProvider } from '@mui/material/styles';
import { Alert, Box, Chip, Grid, TextField, Typography } from '@mui/material';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import { api } from 'common';
import { downloadCsv } from '../common/sharedFunctions';
import CircularLoading from '../components/CircularLoading';
import UserReports from './UserReports';
import localization from '../components/Table/Localization';
import C2Dialog, { C2PrimaryButton, C2SecondaryButton } from '../components/C2Dialog';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2DocsChip,
  C2Select,
  C2StatusChip,
  C2SummaryCard,
} from '../components/Table/C2UserTable';

const STATUS_UI = {
  pending: { tone: 'warning', label: 'Pendiente' },
  in_progress: { tone: 'violet', label: 'En revisión' },
  resolved: { tone: 'success', label: 'Resuelta' },
};
const STATUS_COLORS = { pending: '#D97706', in_progress: C2.violet, resolved: '#16A34A' };

const ROLES = { driver: 'Conductor', customer: 'Usuario' };
const roleLabel = (v) => ROLES[v] || 'Sin tipo';

const REPLY_MAX = 1000;
const QUICK_REPLIES = [
  { label: 'Estamos revisando', text: 'Gracias por escribirnos. Ya estamos revisando tu caso y te daremos una respuesta pronto.' },
  { label: 'Pedir más datos', text: 'Para ayudarte necesitamos más información: indícanos la fecha del viaje y el nombre del conductor o usuario.' },
  { label: 'Caso resuelto', text: 'Tu caso fue resuelto. Gracias por ayudarnos a mejorar Conecta2. Si necesitas algo más, escríbenos de nuevo.' },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontFamily: C2_FONT,
    backgroundColor: '#FFFFFF',
    '& fieldset': { borderColor: C2.border },
    '&:hover fieldset': { borderColor: C2.bright },
    '&.Mui-focused fieldset': { borderColor: C2.violet },
  },
  '& .MuiInputLabel-root': { fontFamily: C2_FONT, color: C2.muted },
  '& .MuiInputLabel-root.Mui-focused': { color: C2.violet },
};

const pillSx = (active, color = C2.violet) => ({
  fontFamily: C2_FONT,
  fontWeight: 700,
  borderRadius: '10px',
  border: `1px solid ${active ? color : C2.border}`,
  color: active ? '#FFFFFF' : C2.dark,
  backgroundColor: active ? color : '#FFFFFF',
  '&:hover': { backgroundColor: active ? color : C2.lavender },
});

const linkChipSx = (bg, fg) => ({ fontFamily: C2_FONT, fontWeight: 700, backgroundColor: bg, color: fg, '& .MuiChip-icon': { color: fg } });

const phoneDigits = (v) => String(v || '').replace(/\D/g, '');
const waNumber = (v) => {
  const d = phoneDigits(v);
  return d.length === 10 ? `57${d}` : d;
};
const hasPhone = (v) => phoneDigits(v).length >= 7;
const fullName = (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || 'Sin nombre';
const isExpoToken = (t) => typeof t === 'string' && /^Expo(nent)?PushToken\[[^\]]+\]$/.test(t.trim());
const responseTimeOf = (r) => {
  const answeredAt = Number(r.repliedAt) || (r.status === 'resolved' ? Number(r.processDate) : 0);
  const created = Number(r.complainDate);
  return answeredAt && created && answeredAt >= created ? answeredAt - created : null;
};
const formatDuration = (ms) => {
  if (ms == null) return '—';
  const hours = ms / 3600000;
  if (hours < 1) return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${Math.round(hours / 24)} días`;
};

function InfoRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: `1px solid ${C2.cellBorder}` }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted, flexShrink: 0 }}>{label}</Typography>
      <Box sx={{ fontFamily: C2_FONT, fontSize: '0.86rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right', wordBreak: 'break-word' }}>{children}</Box>
    </Box>
  );
}

export default function Complain() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const settings = useSelector((s) => s.settingsdata.settings);
  const complaindata = useSelector((s) => s.complaindata);
  const users = useSelector((s) => s.usersdata.users);
  const showContact = !!settings?.AllowCriticalEditsAdmin;

  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [draftStatus, setDraftStatus] = useState('pending');
  const [draftReply, setDraftReply] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState('complains');
  const [reports, setReports] = useState(null);
  const [reportsError, setReportsError] = useState(null);

  useEffect(
    () => api.subscribeUserReports(setReports, (e) => setReportsError(e?.message || 'No se pudieron cargar los reportes.')),
    []
  );
  const pendingReports = (reports || []).filter((r) => r.status === 'pending').length;

  const usersById = useMemo(() => {
    const map = {};
    (users || []).forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [users]);

  const rows = useMemo(
    () =>
      [...(complaindata.list || [])]
        .map((c) => ({ ...c, status: api.complainStatusOf(c) }))
        .sort((a, b) => (Number(b.complainDate) || 0) - (Number(a.complainDate) || 0)),
    [complaindata.list]
  );

  const summary = useMemo(() => {
    const monthStart = moment().startOf('month').valueOf();
    const times = rows.map(responseTimeOf).filter((v) => v != null);
    return {
      pending: rows.filter((r) => r.status === 'pending').length,
      inProgress: rows.filter((r) => r.status === 'in_progress').length,
      resolvedMonth: rows.filter((r) => r.status === 'resolved' && (Number(r.processDate) || 0) >= monthStart).length,
      avgResponse: times.length ? times.reduce((a, b) => a + b, 0) / times.length : null,
    };
  }, [rows]);

  const filtered = rows.filter((r) => (!statusFilter || r.status === statusFilter) && (!roleFilter || r.role === roleFilter));
  const current = openId ? rows.find((r) => r.id === openId) : null;
  const currentUser = current ? usersById[current.uid] : null;
  const canNotify = !!currentUser && isExpoToken(currentUser.pushToken);
  const replyChanged = !!current && draftReply.trim() !== (current.adminReply || '').trim();

  const openComplain = (row) => {
    setOpenId(row.id);
    setDraftStatus(row.status === 'pending' ? 'in_progress' : row.status);
    setDraftReply(row.adminReply || '');
  };

  const save = async () => {
    if (!current) return;
    setSaving(true);
    try {
      await api.updateComplainStatus(current.id, draftStatus, replyChanged ? draftReply : undefined);
      const notified = canNotify && (replyChanged ? !!draftReply.trim() : draftStatus !== current.status);
      setNotice({
        severity: 'success',
        msg: `Queja de ${fullName(current)} marcada como ${STATUS_UI[draftStatus].label.toLowerCase()}.${notified ? ' Le enviamos una notificación a su celular.' : ''}`,
      });
      setOpenId(null);
    } catch (e) {
      setNotice({ severity: 'error', msg: 'No se pudo guardar la queja. Inténtalo de nuevo.' });
    } finally {
      setSaving(false);
    }
  };

  const contactLinks = (row) => {
    if (!showContact) return null;
    const links = [];
    if (hasPhone(row.mobile)) {
      links.push(
        <Chip key="tel" component="a" href={`tel:${phoneDigits(row.mobile)}`} clickable size="small" icon={<PhoneOutlinedIcon />} label="Llamar" sx={linkChipSx(C2.lavender, C2.violet)} />,
        <Chip key="wa" component="a" href={`https://wa.me/${waNumber(row.mobile)}`} target="_blank" rel="noopener noreferrer" clickable size="small" icon={<WhatsAppIcon />} label="WhatsApp" sx={linkChipSx('#DCFCE7', '#15803D')} />
      );
    }
    if (row.email) {
      links.push(
        <Chip
          key="mail"
          component="a"
          href={`mailto:${row.email}?subject=${encodeURIComponent(`Conecta2 · ${row.subject || 'Tu queja'}`)}`}
          clickable
          size="small"
          icon={<MailOutlineRoundedIcon />}
          label="Correo"
          sx={linkChipSx(C2.lavender, C2.violet)}
        />
      );
    }
    return links.length ? <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>{links}</Box> : null;
  };

  const columns = [
    {
      title: 'Fecha',
      field: 'complainDate',
      render: (r) => (
        <Box sx={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.85rem', color: C2.sidebar }}>{r.complainDate ? moment(r.complainDate).format('lll') : '—'}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: r.status === 'pending' ? '#B45309' : C2.muted, fontWeight: r.status === 'pending' ? 700 : 400 }}>
            {r.complainDate ? moment(r.complainDate).fromNow() : ''}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Quién escribe',
      field: 'firstName',
      customFilterAndSearch: (term, r) => `${fullName(r)} ${r.email || ''} ${r.mobile || ''}`.toLowerCase().includes(String(term).toLowerCase()),
      render: (r) => (
        <Box sx={{ textAlign: 'left', minWidth: 150 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{fullName(r)}</Typography>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: C2.muted }}>
            {roleLabel(r.role)}
            {showContact && (r.mobile || r.email) ? ` · ${r.mobile || r.email}` : ''}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Asunto y mensaje',
      field: 'subject',
      customFilterAndSearch: (term, r) => `${r.subject || ''} ${r.body || ''}`.toLowerCase().includes(String(term).toLowerCase()),
      render: (r) => (
        <Box sx={{ textAlign: 'left', maxWidth: 420 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>{r.subject || 'Sin asunto'}</Typography>
          <Typography
            sx={{
              fontFamily: C2_FONT,
              fontSize: '0.78rem',
              color: C2.muted,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {r.body || ''}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Estado',
      field: 'status',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <C2StatusChip tone={STATUS_UI[r.status].tone} label={STATUS_UI[r.status].label} />
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.7rem', color: r.adminReply ? '#15803D' : C2.muted, mt: 0.4, fontWeight: r.adminReply ? 700 : 400 }}>
            {r.adminReply ? 'Respondida' : 'Sin respuesta'}
          </Typography>
        </Box>
      ),
    },
  ];

  const exportCsv = () => {
    const header = ['Fecha', 'Nombre', 'Tipo', 'Correo', 'Celular', 'Asunto', 'Mensaje', 'Estado', 'Respuesta', 'Fecha de respuesta', 'Atendió'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = filtered.map((r) =>
      [
        r.complainDate ? moment(r.complainDate).format('YYYY-MM-DD HH:mm') : '',
        fullName(r),
        roleLabel(r.role),
        r.email,
        r.mobile,
        r.subject,
        r.body,
        STATUS_UI[r.status].label,
        r.adminReply || '',
        r.repliedAt ? moment(r.repliedAt).format('YYYY-MM-DD HH:mm') : '',
        r.attendedByEmail || '',
      ]
        .map(esc)
        .join(',')
    );
    downloadCsv('\uFEFF' + [header.map(esc).join(','), ...lines].join('\n'), `quejas-${moment().format('YYYY-MM-DD')}.csv`);
  };

  const toolbar = c2Toolbar(
    <C2Select
      key="status"
      id="complain-status"
      label="Estado"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={160}
      options={[{ value: '', label: 'Todos' }, ...Object.keys(STATUS_UI).map((k) => ({ value: k, label: STATUS_UI[k].label }))]}
    />,
    <C2Select
      key="role"
      id="complain-role"
      label="Tipo"
      value={roleFilter}
      onChange={(e) => setRoleFilter(e.target.value)}
      minWidth={150}
      options={[{ value: '', label: 'Todos' }, { value: 'driver', label: 'Conductores' }, { value: 'customer', label: 'Usuarios' }]}
    />,
    showContact && filtered.length ? (
      <C2SecondaryButton key="csv" onClick={exportCsv}>
        Exportar CSV
      </C2SecondaryButton>
    ) : null
  );

  if (complaindata.loading && !complaindata.list) return <CircularLoading />;

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Quejas y sugerencias${summary.pending ? ` (${summary.pending})` : ''}`} onClick={() => setTab('complains')} sx={pillSx(tab === 'complains')} />
          <Chip label={`Reporte de usuarios${pendingReports ? ` (${pendingReports})` : ''}`} onClick={() => setTab('reports')} sx={pillSx(tab === 'reports')} />
        </Box>
        {tab === 'reports' ? (
          <UserReports reports={reports} loadError={reportsError} />
        ) : (
        <>
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {notice.msg}
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={MarkEmailUnreadOutlinedIcon} label="Pendientes" value={summary.pending} caption="Nadie las ha revisado" accent={summary.pending ? '#D97706' : C2.violet} />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={ManageSearchOutlinedIcon} label="En revisión" value={summary.inProgress} caption="Un admin las está gestionando" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={TaskAltOutlinedIcon} label="Resueltas este mes" value={summary.resolvedMonth} caption={`${rows.length} quejas en total`} accent="#16A34A" />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard icon={TimerOutlinedIcon} label="Tiempo de respuesta" value={formatDuration(summary.avgResponse)} caption="Promedio hasta responder o resolver" />
          </Grid>
        </Grid>

        <C2TableCard>
          <C2SectionHeading
            title="Quejas y sugerencias"
            subtitle="Llegan desde Ajustes › Quejas en la app. Tu respuesta aparece en la app del usuario y le llega como notificación."
          />
          <MaterialTable
            title={t('complain_title')}
            columns={columns}
            data={filtered}
            style={c2TableStyle(isRTL)}
            components={toolbar}
            onRowClick={(evt, row) => {
              setSelectedRow(row.tableData.id);
              openComplain(row);
            }}
            options={{
              ...c2TableOptions(selectedRow),
              actionsColumnIndex: -1,
              pageSize: 10,
              pageSizeOptions: [10, 20, 50],
              rowStyle: (row) => ({
                backgroundColor: row.status === 'pending' ? '#FFFBEB' : selectedRow === row.tableData.id ? C2.lavender : '#FFFFFF',
                fontFamily: C2_FONT,
              }),
            }}
            localization={c2Localization(
              localization(t),
              <C2EmptyState
                icon={AnnouncementOutlinedIcon}
                title={statusFilter || roleFilter ? 'No hay quejas con estos filtros' : 'Aún no hay quejas'}
                message="Cuando un conductor o usuario escriba desde la app, su mensaje aparecerá aquí para que lo respondas."
              />
            )}
            actions={[
              (row) => ({
                icon: () => <C2DocsChip icon={ReplyRoundedIcon} label={row.adminReply ? 'Ver' : 'Responder'} />,
                tooltip: row.adminReply ? 'Ver detalle' : 'Responder queja',
                onClick: (e, r) => openComplain(r),
              }),
            ]}
          />
        </C2TableCard>

        <C2Dialog
          open={!!current}
          onClose={() => (saving ? null : setOpenId(null))}
          title={current ? current.subject || 'Queja' : 'Queja'}
          icon={AnnouncementOutlinedIcon}
          maxWidth={720}
          actions={
            <>
              <C2SecondaryButton onClick={() => setOpenId(null)} disabled={saving}>
                Cerrar
              </C2SecondaryButton>
              <C2PrimaryButton onClick={save} disabled={saving || !current || (draftStatus === current.status && !replyChanged)}>
                {saving ? 'Guardando…' : replyChanged && draftReply.trim() ? 'Enviar respuesta' : 'Guardar'}
              </C2PrimaryButton>
            </>
          }
        >
          {current ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.8rem', color: C2.muted, mb: 0.75 }}>Mensaje</Typography>
                <Box
                  sx={{
                    p: 1.75,
                    borderRadius: '14px',
                    backgroundColor: C2.lavender,
                    border: `1px solid ${C2.border}`,
                    fontFamily: C2_FONT,
                    fontSize: '0.9rem',
                    color: C2.sidebar,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 260,
                    overflowY: 'auto',
                  }}
                >
                  {current.body || 'Sin mensaje'}
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoRow label="De">{fullName(current)}</InfoRow>
                <InfoRow label="Tipo">{roleLabel(current.role)}</InfoRow>
                <InfoRow label="Fecha">{current.complainDate ? `${moment(current.complainDate).format('lll')} (${moment(current.complainDate).fromNow()})` : '—'}</InfoRow>
                <InfoRow label="Correo">{showContact ? current.email || '—' : t('hidden_demo')}</InfoRow>
                <InfoRow label="Celular">{showContact ? current.mobile || '—' : t('hidden_demo')}</InfoRow>
                <InfoRow label="Estado">
                  <C2StatusChip tone={STATUS_UI[current.status].tone} label={STATUS_UI[current.status].label} />
                </InfoRow>
                {current.statusUpdatedAt ? (
                  <InfoRow label="Última gestión">
                    {moment(current.statusUpdatedAt).format('lll')}
                    {showContact && current.attendedByEmail ? ` · ${current.attendedByEmail}` : ''}
                  </InfoRow>
                ) : null}
                <Box sx={{ mt: 1.5 }}>{contactLinks(current)}</Box>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar, mb: 1 }}>Estado</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {Object.keys(STATUS_UI).map((k) => (
                    <Chip key={k} label={STATUS_UI[k].label} onClick={() => setDraftStatus(k)} sx={pillSx(draftStatus === k, STATUS_COLORS[k])} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.86rem', color: C2.sidebar }}>Respuesta para el usuario</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {QUICK_REPLIES.map((q) => (
                      <Chip key={q.label} size="small" label={q.label} onClick={() => setDraftReply(q.text)} sx={pillSx(draftReply === q.text)} />
                    ))}
                  </Box>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  placeholder="Escribe lo que verá el usuario en la app…"
                  value={draftReply}
                  onChange={(e) => setDraftReply(e.target.value.slice(0, REPLY_MAX))}
                  helperText={`${draftReply.length}/${REPLY_MAX}${current.repliedAt ? ` · Última respuesta: ${moment(current.repliedAt).format('lll')}` : ''}`}
                  sx={fieldSx}
                />
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.25,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: canNotify ? '#F0FDF4' : '#FFFBEB',
                    border: `1px solid ${canNotify ? '#BBF7D0' : '#FDE68A'}`,
                  }}
                >
                  {canNotify ? <NotificationsActiveOutlinedIcon sx={{ color: '#15803D' }} /> : <NotificationsOffOutlinedIcon sx={{ color: '#B45309' }} />}
                  <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: canNotify ? '#166534' : '#92400E' }}>
                    {canNotify
                      ? 'Al guardar, le llegará una notificación a su celular con tu respuesta o el nuevo estado.'
                      : 'Este usuario no tiene la app activa en ningún celular: no recibirá notificación, pero verá la respuesta cuando entre a Quejas.'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          ) : null}
        </C2Dialog>
        </>
        )}
      </ThemeProvider>
    </Box>
  );
}
